import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Mail, Trash2, Send, ChevronDown, ChevronUp, Search, MessageSquare, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/admin/messages`);
      setMessages(res.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (messageId) => {
    if (!replyText.trim()) {
      toast.error('Please enter a reply');
      return;
    }
    setReplying(true);
    try {
      await axios.post(`${API}/admin/messages/${messageId}/reply`, { message: replyText.trim() });
      toast.success('Reply sent! Customer will be notified by email.');
      setReplyText('');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setReplying(false);
    }
  };

  const handleMarkRead = async (messageId) => {
    try {
      await axios.put(`${API}/admin/messages/${messageId}/read`);
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, read: true } : m));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Delete this conversation? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/messages/${messageId}`);
      toast.success('Message deleted');
      setMessages(prev => prev.filter(m => m.id !== messageId));
      if (expandedId === messageId) setExpandedId(null);
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const toggleExpand = (messageId) => {
    if (expandedId === messageId) {
      setExpandedId(null);
    } else {
      setExpandedId(messageId);
      const msg = messages.find(m => m.id === messageId);
      if (msg && !msg.read) handleMarkRead(messageId);
    }
  };

  const filtered = messages.filter(m => {
    const matchesSearch = m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.subject.toLowerCase().includes(searchTerm.toLowerCase());
    if (filter === 'unread') return matchesSearch && !m.read;
    if (filter === 'replied') return matchesSearch && m.replies && m.replies.length > 0;
    return matchesSearch;
  });

  const unreadCount = messages.filter(m => !m.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-messages">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-serif font-bold text-foreground">Messages</h2>
          <p className="text-muted-foreground">
            {messages.length} conversations {unreadCount > 0 && `(${unreadCount} unread)`}
          </p>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or subject..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="messages-search"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'unread', 'replied'].map(f => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {f === 'unread' && unreadCount > 0 && (
                <Badge variant="destructive" className="ml-1.5 text-xs px-1.5">{unreadCount}</Badge>
              )}
            </Button>
          ))}
        </div>
      </div>

      {/* Messages List */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg">{searchTerm || filter !== 'all' ? 'No messages match your filter' : 'No messages yet'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(msg => (
            <Card
              key={msg.id}
              className={`transition-all ${!msg.read ? 'border-primary/50 bg-primary/5' : ''}`}
              data-testid={`message-card-${msg.id}`}
            >
              <div
                className="p-4 cursor-pointer"
                onClick={() => toggleExpand(msg.id)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {!msg.read && (
                        <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                      )}
                      <h4 className="font-semibold text-foreground truncate">{msg.name}</h4>
                      <span className="text-xs text-muted-foreground flex-shrink-0">
                        {new Date(msg.updated_at || msg.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{msg.email}</p>
                    <p className="font-medium text-sm mt-1 truncate">{msg.subject}</p>
                    {msg.replies && msg.replies.length > 0 && (
                      <Badge variant="outline" className="mt-1 text-xs">
                        {msg.replies.length} {msg.replies.length === 1 ? 'reply' : 'replies'}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={(e) => { e.stopPropagation(); handleDelete(msg.id); }}
                      data-testid={`delete-msg-${msg.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    {expandedId === msg.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                </div>
              </div>

              {/* Expanded Conversation */}
              {expandedId === msg.id && (
                <CardContent className="pt-0 border-t">
                  {/* Original message */}
                  <div className="py-4 space-y-4">
                    <div className="bg-muted/50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded">Customer</span>
                        <span className="text-xs text-muted-foreground">{new Date(msg.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                    </div>

                    {/* Replies thread */}
                    {msg.replies && msg.replies.map(reply => (
                      <div
                        key={reply.id}
                        className={`rounded-lg p-4 ${
                          reply.sender === 'admin'
                            ? 'bg-primary/10 border-l-4 border-primary ml-4'
                            : 'bg-muted/50 mr-4'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                            reply.sender === 'admin'
                              ? 'bg-primary/20 text-primary'
                              : 'bg-muted text-muted-foreground'
                          }`}>
                            {reply.sender === 'admin' ? 'Admin' : 'Customer'}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {reply.sender_name} - {new Date(reply.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{reply.message}</p>
                      </div>
                    ))}

                    {/* Reply form */}
                    <div className="flex gap-2 pt-2" data-testid={`reply-form-${msg.id}`}>
                      <Input
                        placeholder="Type your reply..."
                        value={expandedId === msg.id ? replyText : ''}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleReply(msg.id)}
                        disabled={replying}
                        data-testid={`reply-input-${msg.id}`}
                      />
                      <Button
                        onClick={() => handleReply(msg.id)}
                        disabled={replying || !replyText.trim()}
                        className="btn-accent flex-shrink-0"
                        data-testid={`reply-send-${msg.id}`}
                      >
                        {replying ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMessages;
